import React, { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import { YhBox } from "@/components/YhBox";
import { YhFormBox } from "@/components/YhFormBox";
import { IYhFormBox, IYhFormItemsBox, FormItemTypeEnum, IYhFormRef } from "@/components/YhFormBox/Types";
import { useSupplierModel } from "../supplierModel";
import useNavModel, { RouterParamType } from "@/model/navModel";
import { getFormValidatorErrText } from "@/utils/utils"
import { useDictModel, ITreeSelectData } from "../../dict/dictModel";
import { DictCodeEnmu } from "@/types/AppDictEnums";
import moment from 'moment';
import useGlobalModel from '@/model/globalModel';

export const BaseSupplierDetails = () => {
    const {
        addFormVal,
        editFormVal,
        updateAddFormVal,
        updateEditFormVal,
        insertSupplier,
        editSupplier,
        getSupplierOne
    } = useSupplierModel();

    const { selectedNavId, replaceNav, getSelectedNavState, findNav } = useNavModel(({ selectedNavId }) => [selectedNavId]);
    const { isEditable } = useGlobalModel(() => []);
    const formRef = useRef<IYhFormRef>();
    const [detailsInfo] = useState<RouterParamType>(getSelectedNavState() as RouterParamType);
    const { getSelectedDictOne, getTreeSelectOne } = useDictModel(() => []);

    //编辑时， 没有缓存调用详情接口
    useEffect(() => {
        if (!Object.keys(editFormVal).length && detailsInfo.mode === "edit") {
            getSupplierOne(detailsInfo.id)
        }
    }, [detailsInfo])

    //清楚缓存
    useEffect(() => {
        return () => {
            !findNav("7-3-1") && updateAddFormVal(null);
            !findNav("7-3-2") && updateEditFormVal(null);
        }
    }, [selectedNavId])

    //关闭当前页
    const onClose = () => {
        replaceNav(selectedNavId, "7-3");
    }

    //重置
    const onReset = () => {
        formRef.current?.resetFields();
        if (detailsInfo.mode === "edit") {
            getSupplierOne(detailsInfo.id)
        } else {
            updateAddFormVal(null);
        }
    };

    //表单提交
    const onSubmit = async () => {
        formRef.current?.validateFields().then(val => {
            let data = {
                ...val,
                name: val.name.trim(),
                contractStartTime: val.contractStartTime ? moment(val.contractStartTime).format("YYYY-MM-DD HH:mm:ss") : '',
                contractEndTime:val.contractEndTime ? moment(val.contractEndTime).format("YYYY-MM-DD HH:mm:ss") : '',
                qualifications: val.qualifications ? JSON.stringify(val.qualifications) : ""
            }
            if (detailsInfo.mode === "edit") {
                editSupplier({ ...data, id: detailsInfo.id }).then(res => {
                    res && onClose();
                    updateEditFormVal(null);
                })
            } else {
                insertSupplier(data).then(res => {
                    res && onClose();
                    updateAddFormVal(null);
                })
            }
        }).catch(err => {
            message.warning(getFormValidatorErrText(err));
        })
    }
    //保留缓存及回显表单
    const saveCache = (val: { [key: string]: any }, isFormItem: boolean = true) => {
        isFormItem && formRef.current?.setFieldsValue(val);
        detailsInfo.mode === "edit" ? updateEditFormVal(val) : updateAddFormVal(val);
    }

    //图片变更缓存保留  保留缓存及回显表单
    const onSetFieldsValue = (formValues: { [key: string]: any }) => {
        detailsInfo.mode === "edit" ? updateEditFormVal(formValues) : updateAddFormVal(formValues);
    }

    //供应商类型
    const typeCodeChange = (value: string, node: ITreeSelectData) => {
        node && saveCache({ categoryName: node.title, categoryCode: node.value });
    }

    const onChange = (changedValues: { [key: string]: any }) => {
        saveCache(changedValues, false)
    }

    const baseFormItem: IYhFormItemsBox = {
        title: "基础信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "供应商编码",
                fieldName: "code",
                disable: true,
                placeholder: "自动生成"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "供应商名称",
                fieldName: "name",
                rules: [{ required: true, message: "请输入供应商名称" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系人",
                fieldName: "contact",
                rules: [{ required: true, message: "请输入联系人" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系电话",
                fieldName: "phone",
                rules: [{ required: true, message: "请输入联系人电话" }]
            }, {
                type: FormItemTypeEnum.TreeSelect,
                fieldLabel: "供应商类型",
                fieldName: "categoryCode",
                onTreeSelectChange: typeCodeChange,
                treeData: getTreeSelectOne(DictCodeEnmu.SUPPLIER_TYPE),
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "电子邮箱",
                fieldName: "email"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "办公电话",
                fieldName: "tel"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系地址",
                fieldName: "address",
                rules: [{ required: true, message: "请选择联系地址" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "邮编",
                fieldName: "postcode",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "供应商编码生成",
                fieldName: "abbreviation",
                rules: [{ required: true, message: "请输入供应商编码生成" }]
            },
            {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "合同签订日期",
                fieldName: "contractStartTime"
            },
            {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "合同到期日期",
                fieldName: "contractEndTime"
            },
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "备注",
                fieldName: "note",
                span: 12,
                labelCol: { span: 4 }
            },
            {
                type: FormItemTypeEnum.Switch,
                fieldLabel: "客户状态",
                fieldName: "status"
            },

            //带出字段
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "供应商类型名称",
                fieldName: "categoryName",
                hidden: true
            },
        ]
    };

    const priceFormItem: IYhFormItemsBox = {
        title: "开票信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "公司名称",
                fieldName: "invoiceTitle",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "开户行地址",
                fieldName: "bankAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "银行账户",
                fieldName: "bankAcct"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "邮寄地址",
                fieldName: "postAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "公司注册地址",
                fieldName: "registeredAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "电话",
                fieldName: "bankPhone"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "统一社会信用代码",
                fieldName: "licenseNo"
            },
        ]
    }
    // 资质文件
    const fileFormItem: IYhFormItemsBox = {
        title: '资质信息',
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.FileImage,
                fieldLabel: "资质文件",
                fieldName: "qualifications",
                colClassName: "file-image-box ant-col-h-auto",
                span: 24,
                md5List: detailsInfo.mode === "edit" ? editFormVal.qualifications ?? [] : addFormVal.qualifications ?? [],
                onSetFieldsValue
            }
        ]
    }
    const otherFormItem: IYhFormItemsBox = {
        title: "其它信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "创建人",
                fieldName: "createUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "创建日期",
                fieldName: "createTime",
                disable: isEditable
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "修改人",
                fieldName: "updateUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "修改日期",
                fieldName: "updateTime",
                disable: isEditable
            }
        ]
    }

    const supplierFromConfig: IYhFormBox<any> = {
        formRef,
        labelCol: { span: 8 },
        labelAlign: "right",
        formValues: detailsInfo.mode === "edit" ? editFormVal : addFormVal,
        onChange,
        boxs: [baseFormItem, priceFormItem, fileFormItem, otherFormItem]
    }


    const action = <>
        {/* <YhAuth resCode={detailsInfo.mode === "edit" ? ResourceAuthEnum.DATA_S_ED_SAVE : ResourceAuthEnum.DATA_S_AD_SAVE}> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onSubmit}
        >保存</Button>
        {/* </YhAuth> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onReset}
        >重置</Button>
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onClose}
        >取消</Button>
    </>;

    return (
        <>
            <YhBox title={`供应商${detailsInfo.mode === "edit" ? "编辑" : "新增"}`} action={action} style={{ paddingBottom: "0px", marginBottom: "10px" }}></YhBox>
            <YhFormBox {...supplierFromConfig} />
        </>
    )
}